import React, { useState, useMemo } from 'react';
import { Search, Users, Receipt, Car, FileText, Calendar, Wrench, ChevronDown, ChevronRight, MapPin, Phone, Mail, AlertCircle, Wallet } from 'lucide-react';
import { useData } from '../context/DataContext';
import { voertuigTypeMeta } from '../components/AutoEditor';
import { isApkExempt } from '../lib/apk';

import { useT } from '../context/LanguageContext';
const eur = (n) => (Number(n) || 0).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' });
const eurShort = (n) => (Number(n) || 0).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const isKentekenLike = (s) => /^[a-zA-Z0-9-]{2,8}$/.test(s.trim());

export default function Zoeken() {

  const { t } = useT();  const { klanten, bonnen, handelAutos, documenten, klantAutos, planning } = useData();
  const [q, setQ] = useState('');

  const ql = q.trim().toLowerCase();
  const normKenteken = (s) => (s || '').replace(/[-\s]/g, '').toUpperCase();
  const qNorm = normKenteken(q);

  // Vind exact match op kenteken (klantAuto eerst, dan handelAuto)
  const matchedVehicle = useMemo(() => {
    if (!qNorm || !isKentekenLike(q)) return null;
    const ka = klantAutos.find(a => normKenteken(a.kenteken) === qNorm);
    if (ka) return { ...ka, _source: 'klant' };
    const ha = handelAutos.find(a => normKenteken(a.kenteken) === qNorm);
    if (ha) return { ...ha, _source: 'handel' };
    return null;
  }, [qNorm, q, klantAutos, handelAutos]);

  const vehicleKlant = matchedVehicle?._source === 'klant'
    ? klanten.find(k => k.id === matchedVehicle.klantId)
    : null;

  // Alle bonnen voor dit kenteken (chronologisch nieuwste eerst)
  const vehicleHistory = useMemo(() => {
    if (!matchedVehicle) return [];
    return bonnen
      .filter(b => normKenteken(b.kenteken) === qNorm)
      .slice()
      .sort((a, b) => (b.datum || '').localeCompare(a.datum || ''));
  }, [matchedVehicle, qNorm, bonnen]);

  const vehiclePlanning = useMemo(() => {
    if (!matchedVehicle) return [];
    return planning
      .filter(p => normKenteken(p.kenteken || '') === qNorm)
      .slice()
      .sort((a, b) => (b.start || '').localeCompare(a.start || ''));
  }, [matchedVehicle, qNorm, planning]);

  const vehicleDocs = useMemo(() => {
    if (!matchedVehicle) return [];
    return documenten.filter(d => normKenteken(d.kenteken || '') === qNorm);
  }, [matchedVehicle, qNorm, documenten]);

  // KPI's voor de voertuig-historie
  const historyStats = useMemo(() => {
    const facturen = vehicleHistory.filter(b => b.type === 'Factuur' || b.type === 'Werkbon');
    const totaalOmzet = facturen.reduce((s, b) => s + (b.totaal || 0), 0);
    const totaalOpenstaand = vehicleHistory.reduce((s, b) => s + (b.openstaand || 0), 0);
    const aantalReparaties = vehicleHistory.filter(b => b.type === 'Werkbon' || b.type === 'Factuur').length;
    const aantalOffertes = vehicleHistory.filter(b => b.type === 'Offerte').length;
    const laatsteBezoek = vehicleHistory[0]?.datum;
    const eerstebezoek = vehicleHistory[vehicleHistory.length - 1]?.datum;
    const gemiddelde = aantalReparaties > 0 ? totaalOmzet / aantalReparaties : 0;
    // Verzamel alle vervangen onderdelen
    const onderdelen = [];
    vehicleHistory.forEach(b => {
      (b.regels || []).forEach(r => {
        if (r.type === 'Onderdeel' || r.type === 'Materiaal') {
          onderdelen.push({ omschr: r.omschr, aantal: r.aantal, datum: b.datum, bonNummer: b.nummer });
        }
      });
    });
    return { totaalOmzet, totaalOpenstaand, aantalReparaties, aantalOffertes, laatsteBezoek, eerstebezoek, gemiddelde, onderdelen };
  }, [vehicleHistory]);

  // Generieke zoekresultaten (zonder exacte kenteken match — voor naam, plaats, omschrijving etc.)
  const generic = useMemo(() => {
    if (!ql || matchedVehicle) return { klanten: [], bonnen: [], autos: [], docs: [] };
    return {
      klanten: klanten.filter(k =>
        k.naam.toLowerCase().includes(ql) || (k.telefoon || '').includes(ql) ||
        (k.email || '').toLowerCase().includes(ql) || (k.plaats || '').toLowerCase().includes(ql) ||
        (k.kentekens || []).some(kt => normKenteken(kt).includes(qNorm))
      ).slice(0, 10),
      bonnen: bonnen.filter(b =>
        (b.nummer || '').toLowerCase().includes(ql) ||
        (b.omschrijving || '').toLowerCase().includes(ql) ||
        normKenteken(b.kenteken).includes(qNorm)
      ).slice(0, 15),
      autos: [
        ...handelAutos.filter(a => normKenteken(a.kenteken).includes(qNorm) || (a.merk || '').toLowerCase().includes(ql) || (a.model || '').toLowerCase().includes(ql)),
        ...klantAutos.filter(a => normKenteken(a.kenteken).includes(qNorm) || (a.merk || '').toLowerCase().includes(ql) || (a.model || '').toLowerCase().includes(ql)),
      ].slice(0, 10),
      docs: documenten.filter(d =>
        (d.naam || '').toLowerCase().includes(ql) || normKenteken(d.kenteken || '').includes(qNorm)
      ).slice(0, 10),
    };
  }, [ql, qNorm, matchedVehicle, klanten, bonnen, handelAutos, klantAutos, documenten]);

  const total = matchedVehicle ? vehicleHistory.length : (generic.klanten.length + generic.bonnen.length + generic.autos.length + generic.docs.length);

  return (
    <div data-testid="zoeken-page">
      <div className="cs-page-header">
        <div><h2>{t('page.search.title')}</h2><p>{t('page.search.sub')}</p></div>
      </div>

      <div className="cs-card padded" style={{ marginBottom: 14 }}>
        <div style={{ position: 'relative' }}>
          <Search size={18} style={{ position: 'absolute', left: 14, top: 13, color: 'var(--cs-text-muted)' }} />
          <input data-testid="zoeken-input" className="cs-input" autoFocus style={{ paddingLeft: 42, fontSize: 15, padding: '12px 12px 12px 42px' }} placeholder={t('Bijv. 12-AB-34, Jan de Vries, of B-2025-0042...')} value={q} onChange={e => setQ(e.target.value)} />
        </div>
        {q && <div style={{ marginTop: 8, fontSize: 12, color: 'var(--cs-text-muted)' }}>
          {matchedVehicle
            ? <>📋 <b>{vehicleHistory.length} {t('bonnen')}</b> {t('gevonden voor kenteken')} <span style={{ fontFamily: 'ui-monospace, monospace' }}>{matchedVehicle.kenteken}</span></>
            : <>{total} {t('resultaten gevonden')}</>}
        </div>}
      </div>

      {matchedVehicle && (
        <VehicleHistoryView
          vehicle={matchedVehicle}
          klant={vehicleKlant}
          history={vehicleHistory}
          planning={vehiclePlanning}
          docs={vehicleDocs}
          stats={historyStats}
        />
      )}

      {q && !matchedVehicle && (
        <>
          {generic.klanten.length > 0 && (
            <div className="cs-card padded" style={{ marginBottom: 12 }}>
              <h3 className="cs-card-title"><Users size={16} /> {t('Klanten')} ({generic.klanten.length})</h3>
              <table className="cs-table">
                <tbody>
                  {generic.klanten.map(k => (
                    <tr key={k.id} onClick={() => setQ((k.kentekens || [])[0] || k.naam)} style={{ cursor: 'pointer' }}>
                      <td style={{ fontWeight: 600 }}>{k.naam}</td>
                      <td>{k.telefoon}</td>
                      <td>{k.plaats}</td>
                      <td style={{ fontFamily: 'ui-monospace, monospace' }}>{(k.kentekens || []).join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {generic.autos.length > 0 && (
            <div className="cs-card padded" style={{ marginBottom: 12 }}>
              <h3 className="cs-card-title"><Car size={16} /> {t('Voertuigen')} ({generic.autos.length}) <span style={{ fontSize: 11, color: 'var(--cs-text-muted)', fontWeight: 400 }}>— {t('klik op een kenteken voor volledige historie', 'klik op een kenteken voor volledige historie')}</span></h3>
              <table className="cs-table">
                <tbody>
                  {generic.autos.map((a, i) => (
                    <tr key={i} onClick={() => setQ(a.kenteken)} style={{ cursor: 'pointer' }}>
                      <td style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 600, color: 'var(--cs-accent)' }}>{a.kenteken}</td>
                      <td>{a.merk} {a.model}</td>
                      <td>{a.bouwjaar || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {generic.bonnen.length > 0 && (
            <div className="cs-card padded" style={{ marginBottom: 12 }}>
              <h3 className="cs-card-title"><Receipt size={16} /> {t('nav.receipts')} ({generic.bonnen.length})</h3>
              <table className="cs-table">
                <tbody>
                  {generic.bonnen.map(b => (
                    <tr key={b.id} onClick={() => b.kenteken && setQ(b.kenteken)} style={{ cursor: 'pointer' }}>
                      <td style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 600 }}>{b.nummer}</td>
                      <td><span className="cs-badge blue">{b.type}</span></td>
                      <td style={{ fontFamily: 'ui-monospace, monospace', color: 'var(--cs-accent)' }}>{b.kenteken}</td>
                      <td>{b.omschrijving}</td>
                      <td>{b.datum ? new Date(b.datum).toLocaleDateString('nl-NL') : '-'}</td>
                      <td style={{ textAlign: 'right', fontWeight: 600 }}>{eur(b.totaal)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {generic.docs.length > 0 && (
            <div className="cs-card padded">
              <h3 className="cs-card-title"><FileText size={16} /> {t('Documenten')} ({generic.docs.length})</h3>
              <table className="cs-table">
                <tbody>
                  {generic.docs.map(d => (
                    <tr key={d.id}><td style={{ fontWeight: 600 }}>{d.naam}</td><td>{d.type}</td><td>{d.datum ? new Date(d.datum).toLocaleDateString('nl-NL') : '-'}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {total === 0 && <div className="cs-card padded cs-empty">{t('Geen resultaten gevonden voor', 'Geen resultaten gevonden voor')} &quot;{q}&quot;.</div>}
        </>
      )}
    </div>
  );
}

function VehicleHistoryView({ vehicle, klant, history, planning, docs, stats }) {
  const { t } = useT();
  const [expanded, setExpanded] = useState({});
  const toggle = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));

  // Doorzoekbare artikelhistorie: alle regels uitgeklapt over alle bonnen (zoals CarSys)
  const [artQ, setArtQ] = useState('');
  const [artType, setArtType] = useState('Alles');
  const alleRegels = useMemo(() => {
    const rows = [];
    history.forEach(b => (b.regels || []).forEach(r => rows.push({
      datum: b.datum, km: b.km, code: r.code || '', omschr: r.omschr || '',
      type: r.type || '', aantal: r.aantal, prijs: r.prijs, bonNummer: b.nummer, bonType: b.type,
    })));
    return rows.sort((a, b) => (b.datum || '').localeCompare(a.datum || ''));
  }, [history]);
  const artTypes = useMemo(() => ['Alles', ...Array.from(new Set(alleRegels.map(r => r.type).filter(Boolean)))], [alleRegels]);
  const artFiltered = useMemo(() => {
    const f = artQ.trim().toLowerCase();
    return alleRegels.filter(r =>
      (artType === 'Alles' || r.type === artType) &&
      (!f || r.omschr.toLowerCase().includes(f) || (r.code || '').toLowerCase().includes(f))
    );
  }, [alleRegels, artQ, artType]);

  const apkDagen = vehicle.apkVervalt
    ? Math.ceil((new Date(vehicle.apkVervalt) - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <div data-testid="vehicle-history-view">
      {/* Voertuig + klant info hero */}
      <div className="mc-vh-hero cs-card padded" data-testid="vh-hero">
        <div className="mc-vh-plate">{vehicle.kenteken}</div>
        <div className="mc-vh-info">
          <h3 style={{ margin: 0, fontSize: 20, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 24 }}>{voertuigTypeMeta(vehicle.voertuigType).icon}</span>
            {vehicle.merk} {vehicle.model}
            <span className="cs-badge gray" style={{ fontSize: 11 }}>{voertuigTypeMeta(vehicle.voertuigType).label}</span>
          </h3>
          <div style={{ color: 'var(--cs-text-muted)', fontSize: 13, marginTop: 2 }}>
            {vehicle.bouwjaar ? `${t('Bouwjaar')} ${vehicle.bouwjaar}` : ''}
            {vehicle.brandstof ? ` · ${vehicle.brandstof}` : ''}
            {vehicle.kleur ? ` · ${vehicle.kleur}` : ''}
            {vehicle.km != null ? ` · ${(vehicle.km || 0).toLocaleString('nl-NL')} km` : ''}
          </div>
          <div style={{ display: 'flex', gap: 14, marginTop: 10, flexWrap: 'wrap' }}>
            {isApkExempt(vehicle) ? (
              <div style={{ fontSize: 12, color: 'var(--cs-text-muted)' }}>
                <span className="cs-badge gray">{t('APK vrijgesteld')}</span>
              </div>
            ) : vehicle.apkVervalt && (
              <div style={{ fontSize: 12 }}>
                <span style={{ color: 'var(--cs-text-muted)' }}>{t('APK vervalt:')} </span>
                <b>{new Date(vehicle.apkVervalt).toLocaleDateString('nl-NL')}</b>
                {apkDagen != null && (
                  <span className={`cs-badge ${apkDagen < 0 ? 'red' : apkDagen < 30 ? 'red' : apkDagen < 60 ? 'amber' : 'green'}`} style={{ marginLeft: 8 }}>
                    {apkDagen < 0 ? `${Math.abs(apkDagen)} ${t('dagen verlopen')}` : `${apkDagen} ${t('dagen')}`}
                  </span>
                )}
              </div>
            )}
            {vehicle._source === 'handel' && <span className="cs-badge blue">{t('Handelvoorraad')}</span>}
          </div>
        </div>
        {klant && (
          <div className="mc-vh-klant">
            <div style={{ fontSize: 11, color: 'var(--cs-text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, marginBottom: 6 }}>{t('Eigenaar')}</div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{klant.naam}</div>
            {klant.telefoon && <div style={{ fontSize: 12.5, color: 'var(--cs-text-muted)', display: 'flex', gap: 6, alignItems: 'center', marginTop: 4 }}><Phone size={12} />{klant.telefoon}</div>}
            {klant.email && <div style={{ fontSize: 12.5, color: 'var(--cs-text-muted)', display: 'flex', gap: 6, alignItems: 'center', marginTop: 2 }}><Mail size={12} />{klant.email}</div>}
            {klant.plaats && <div style={{ fontSize: 12.5, color: 'var(--cs-text-muted)', display: 'flex', gap: 6, alignItems: 'center', marginTop: 2 }}><MapPin size={12} />{klant.plaats}</div>}
          </div>
        )}
      </div>

      {/* KPI grid */}
      <div className="cs-kpi-grid" style={{ marginTop: 14, marginBottom: 14 }}>
        <div className="cs-kpi" data-testid="vh-kpi-reparaties"><Wrench size={20} className="icon" /><div className="label">{t('Reparaties')}</div><div className="value">{stats.aantalReparaties}</div></div>
        <div className="cs-kpi" data-testid="vh-kpi-offertes"><Receipt size={20} className="icon" /><div className="label">{t('Offertes')}</div><div className="value">{stats.aantalOffertes}</div></div>
        <div className="cs-kpi" data-testid="vh-kpi-omzet"><Wallet size={20} className="icon" /><div className="label">{t('Totale omzet')}</div><div className="value">{eurShort(stats.totaalOmzet)}</div></div>
        <div className="cs-kpi" data-testid="vh-kpi-gemiddelde"><Receipt size={20} className="icon" /><div className="label">{t('Gemiddeld per beurt')}</div><div className="value">{eurShort(stats.gemiddelde)}</div></div>
        {stats.totaalOpenstaand > 0 && (
          <div className="cs-kpi" data-testid="vh-kpi-openstaand"><AlertCircle size={20} className="icon" /><div className="label">{t('Openstaand')}</div><div className="value" style={{ color: 'var(--cs-red)' }}>{eur(stats.totaalOpenstaand)}</div></div>
        )}
        {stats.laatsteBezoek && (
          <div className="cs-kpi" data-testid="vh-kpi-laatste"><Calendar size={20} className="icon" /><div className="label">{t('Laatste bezoek')}</div><div className="value" style={{ fontSize: 18 }}>{new Date(stats.laatsteBezoek).toLocaleDateString('nl-NL')}</div></div>
        )}
      </div>

      {/* Reparatie tijdlijn */}
      <div className="cs-card padded" style={{ marginBottom: 14 }} data-testid="vh-timeline">
        <h3 className="cs-card-title"><Wrench size={16} /> {t('Reparatie-historie')} ({history.length})</h3>
        {history.length === 0 ? (
          <div className="cs-empty">{t('Nog geen bonnen geregistreerd voor dit voertuig.', 'Nog geen bonnen geregistreerd voor dit voertuig.')}</div>
        ) : (
          <div className="mc-vh-timeline">
            {history.map(b => {
              const isOpen = !!expanded[b.id];
              const onderdelen = (b.regels || []).filter(r => r.type === 'Onderdeel' || r.type === 'Materiaal');
              const arbeid = (b.regels || []).filter(r => r.type === 'Arbeid' || r.type === 'Uren');
              return (
                <div key={b.id} className={`mc-vh-bon ${isOpen ? 'open' : ''}`} data-testid={`vh-bon-${b.id}`}>
                  <div className="mc-vh-bon-head" onClick={() => toggle(b.id)}>
                    <div className="mc-vh-bon-date">
                      <div className="mc-vh-day">{b.datum ? new Date(b.datum).getDate() : '-'}</div>
                      <div className="mc-vh-mon">{b.datum ? new Date(b.datum).toLocaleDateString('nl-NL', { month: 'short' }).toUpperCase() : ''}</div>
                      <div className="mc-vh-yr">{b.datum ? new Date(b.datum).getFullYear() : ''}</div>
                    </div>
                    <div className="mc-vh-bon-body">
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <span className={`cs-badge ${b.type === 'Factuur' ? 'green' : b.type === 'Werkbon' ? 'blue' : 'amber'}`}>{b.type}</span>
                        <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12, fontWeight: 600 }}>{b.nummer}</span>
                        {b.openstaand > 0 && <span className="cs-badge red">{t('Open')}: {eur(b.openstaand)}</span>}
                      </div>
                      <div style={{ marginTop: 4, fontSize: 13 }}>{b.omschrijving || `(${t('geen omschrijving')})`}</div>
                      {!isOpen && (b.regels || []).length > 0 && (
                        <div style={{ marginTop: 4, fontSize: 11.5, color: 'var(--cs-text-muted)' }}>
                          {arbeid.length} {t('arbeid')} · {onderdelen.length} {t('onderdelen')} · {b.regels.length} {t('regels totaal')}
                        </div>
                      )}
                    </div>
                    <div className="mc-vh-bon-tot">
                      <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--cs-accent)' }}>{eur(b.totaal)}</div>
                      {b.km && <div style={{ fontSize: 11, color: 'var(--cs-text-muted)', textAlign: 'right' }}>{b.km.toLocaleString('nl-NL')} km</div>}
                    </div>
                    <div style={{ alignSelf: 'center', color: 'var(--cs-text-muted)' }}>
                      {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                    </div>
                  </div>
                  {isOpen && (b.regels || []).length > 0 && (
                    <div className="mc-vh-bon-detail">
                      <table className="cs-table" style={{ marginTop: 6 }}>
                        <thead><tr><th>{t('Type')}</th><th>{t('Omschrijving')}</th><th style={{ textAlign: 'right' }}>{t('Aantal')}</th><th style={{ textAlign: 'right' }}>{t('Prijs')}</th><th style={{ textAlign: 'right' }}>{t('Totaal')}</th></tr></thead>
                        <tbody>
                          {(b.regels || []).map((r, i) => (
                            <tr key={i}>
                              <td><span className={`cs-badge ${r.type === 'Arbeid' || r.type === 'Uren' ? 'blue' : r.type === 'Onderdeel' || r.type === 'Materiaal' ? 'green' : 'gray'}`}>{r.type}</span></td>
                              <td>{r.omschr}</td>
                              <td style={{ textAlign: 'right' }}>{r.aantal}</td>
                              <td style={{ textAlign: 'right' }}>{eur(r.prijs)}</td>
                              <td style={{ textAlign: 'right', fontWeight: 600 }}>{eur((r.aantal || 1) * (r.prijs || 0))}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          {b.subtotaal != null && <tr><td colSpan={4} style={{ textAlign: 'right' }}>{t('Subtotaal')}</td><td style={{ textAlign: 'right' }}>{eur(b.subtotaal)}</td></tr>}
                          {b.btw != null && b.btw > 0 && <tr><td colSpan={4} style={{ textAlign: 'right' }}>{t('BTW')}</td><td style={{ textAlign: 'right' }}>{eur(b.btw)}</td></tr>}
                          <tr><td colSpan={4} style={{ textAlign: 'right', fontWeight: 700 }}>{t('Totaal')}</td><td style={{ textAlign: 'right', fontWeight: 700, color: 'var(--cs-accent)' }}>{eur(b.totaal)}</td></tr>
                        </tfoot>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Doorzoekbare artikelhistorie (zoals CarSys) */}
      {alleRegels.length > 0 && (
        <div className="cs-card padded" style={{ marginBottom: 14 }} data-testid="vh-artikelhistorie">
          <h3 className="cs-card-title"><Search size={16} /> {t('Artikelhistorie', 'Artikelhistorie')} ({alleRegels.length})
            <span style={{ fontSize: 11, color: 'var(--cs-text-muted)', fontWeight: 400 }}>— {t('alle arbeid + onderdelen over alle bonnen', 'alle arbeid + onderdelen over alle bonnen')}</span>
          </h3>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ position: 'relative', flex: '1 1 240px', minWidth: 200 }}>
              <Search size={15} style={{ position: 'absolute', left: 12, top: 11, color: 'var(--cs-text-muted)' }} />
              <input
                data-testid="vh-artikel-zoek"
                className="cs-input"
                style={{ paddingLeft: 36 }}
                placeholder={t('Zoek artikel of code… (bijv. koppeling, olie)', 'Zoek artikel of code… (bijv. koppeling, olie)')}
                value={artQ}
                onChange={e => setArtQ(e.target.value)}
              />
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {artTypes.map(tp => (
                <button
                  key={tp}
                  data-testid={`vh-artikel-filter-${tp}`}
                  className={`cs-badge ${artType === tp ? 'blue' : 'gray'}`}
                  style={{ cursor: 'pointer', border: 'none', fontSize: 12 }}
                  onClick={() => setArtType(tp)}
                >
                  {tp === 'Alles' ? t('Alles', 'Alles') : tp}
                </button>
              ))}
            </div>
          </div>
          {artFiltered.length === 0 ? (
            <div className="cs-empty">{t('Geen artikelen gevonden voor', 'Geen artikelen gevonden voor')} &quot;{artQ}&quot;.</div>
          ) : (
            <table className="cs-table" data-testid="vh-artikel-tabel">
              <thead><tr>
                <th>{t('Datum')}</th><th>{t('Bon')}</th><th>{t('Type')}</th><th>{t('Code')}</th>
                <th>{t('Omschrijving')}</th><th style={{ textAlign: 'right' }}>{t('Aantal')}</th>
                <th style={{ textAlign: 'right' }}>{t('Prijs')}</th><th style={{ textAlign: 'right' }}>{t('Totaal')}</th>
              </tr></thead>
              <tbody>
                {artFiltered.map((r, i) => (
                  <tr key={i}>
                    <td>{r.datum ? new Date(r.datum).toLocaleDateString('nl-NL') : '-'}</td>
                    <td style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{r.bonNummer}</td>
                    <td><span className={`cs-badge ${r.type === 'Arbeid' || r.type === 'Uren' ? 'blue' : r.type === 'Onderdeel' || r.type === 'Materiaal' ? 'green' : 'gray'}`}>{r.type || '-'}</span></td>
                    <td style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{r.code || '-'}</td>
                    <td>{r.omschr}</td>
                    <td style={{ textAlign: 'right' }}>{r.aantal}</td>
                    <td style={{ textAlign: 'right' }}>{eur(r.prijs)}</td>
                    <td style={{ textAlign: 'right', fontWeight: 600 }}>{eur((r.aantal || 1) * (r.prijs || 0))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Onderdelen gebruikt */}
      {stats.onderdelen.length > 0 && (
        <div className="cs-card padded" style={{ marginBottom: 14 }} data-testid="vh-onderdelen">
          <h3 className="cs-card-title"><Wrench size={16} /> {t('Vervangen onderdelen / materialen')} ({stats.onderdelen.length})</h3>
          <table className="cs-table">
            <thead><tr><th>{t('Datum')}</th><th>{t('Bon')}</th><th>{t('Onderdeel')}</th><th style={{ textAlign: 'right' }}>{t('Aantal')}</th></tr></thead>
            <tbody>
              {stats.onderdelen.map((o, i) => (
                <tr key={i}>
                  <td>{o.datum ? new Date(o.datum).toLocaleDateString('nl-NL') : '-'}</td>
                  <td style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{o.bonNummer}</td>
                  <td>{o.omschr}</td>
                  <td style={{ textAlign: 'right' }}>{o.aantal}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Planning */}
      {planning.length > 0 && (
        <div className="cs-card padded" style={{ marginBottom: 14 }} data-testid="vh-planning">
          <h3 className="cs-card-title"><Calendar size={16} /> {t('Toekomstige + recente afspraken')}</h3>
          <table className="cs-table">
            <thead><tr><th>{t('Datum')}</th><th>{t('Titel')}</th><th>{t('Status')}</th></tr></thead>
            <tbody>
              {planning.slice(0, 8).map(p => (
                <tr key={p.id}>
                  <td>{p.start ? new Date(p.start).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' }) : '-'}</td>
                  <td>{p.titel}</td>
                  <td><span className="cs-badge blue">{t(p.status || 'Gepland')}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Documenten */}
      {docs.length > 0 && (
        <div className="cs-card padded" data-testid="vh-docs">
          <h3 className="cs-card-title"><FileText size={16} /> {t('Documenten')} ({docs.length})</h3>
          <table className="cs-table">
            <tbody>
              {docs.map(d => (
                <tr key={d.id}><td style={{ fontWeight: 600 }}>{d.naam}</td><td>{d.type}</td><td>{d.datum ? new Date(d.datum).toLocaleDateString('nl-NL') : '-'}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <style>{`
        .mc-vh-hero { display: grid; grid-template-columns: auto 1fr auto; gap: 22px; align-items: center; }
        .mc-vh-plate {
          background: linear-gradient(180deg, #fde047 0%, #facc15 100%);
          padding: 14px 22px; border-radius: 8px; font-family: 'Courier New', monospace;
          font-size: 28px; font-weight: 800; letter-spacing: 3px;
          border: 2px solid #0f172a; box-shadow: 0 4px 10px rgba(0,0,0,0.12);
          color: #0f172a;
        }
        .mc-vh-info { min-width: 0; }
        .mc-vh-klant { background: var(--cs-card-alt); padding: 12px 16px; border-radius: 8px; min-width: 220px; }
        .mc-vh-timeline { display: flex; flex-direction: column; gap: 10px; margin-top: 6px; }
        .mc-vh-bon {
          border: 1px solid var(--cs-border); border-radius: 8px; overflow: hidden;
          transition: border-color 140ms, box-shadow 140ms;
        }
        .mc-vh-bon:hover { border-color: var(--cs-accent); box-shadow: 0 2px 8px rgba(37,99,235,.08); }
        .mc-vh-bon.open { border-color: var(--cs-accent); }
        .mc-vh-bon-head {
          display: grid; grid-template-columns: auto 1fr auto auto;
          gap: 14px; padding: 10px 14px; cursor: pointer; align-items: center;
        }
        .mc-vh-bon-date {
          background: var(--cs-card-alt); border-radius: 6px; padding: 6px 10px;
          text-align: center; min-width: 56px;
        }
        .mc-vh-day { font-size: 18px; font-weight: 800; line-height: 1; color: var(--cs-accent); }
        .mc-vh-mon { font-size: 10.5px; font-weight: 600; color: var(--cs-text-muted); letter-spacing: 1px; }
        .mc-vh-yr { font-size: 10.5px; color: var(--cs-text-muted); }
        .mc-vh-bon-body { min-width: 0; }
        .mc-vh-bon-tot { text-align: right; min-width: 100px; }
        .mc-vh-bon-detail { background: var(--cs-card-alt); padding: 4px 14px 12px; border-top: 1px dashed var(--cs-border); }
        @media (max-width: 900px) {
          .mc-vh-hero { grid-template-columns: 1fr; }
          .mc-vh-klant { width: 100%; }
          .mc-vh-bon-head { grid-template-columns: auto 1fr; }
          .mc-vh-bon-tot { grid-column: 2 / 3; text-align: left; }
        }
      `}</style>
    </div>
  );
}
